import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import PersonIcon from '@mui/icons-material/Person';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ScheduleSession, ScheduleSessionStatus } from '../hooks/usePassScheduleData';
import { useManagePasses } from '../ManagePassesContext';
import { getTimeFormat } from '../../utils/getDateAndTimeFormat';
import { ParallelActivityItem, PassService } from '../../api';
import {
  getEffectiveNumPunches,
  getPassOrderPage,
  getUsedPunches,
  isPassActiveForDate,
  isRecurringMonthly,
} from '../utils/passUtils';
import { getPassCancelHoursBefore } from '../utils/passCancellationUtils';
import { BottomSheet } from './BottomSheet';
import { useLanguage } from '../../utils/GeneralUtils';
import { RotatingLoader } from '../../ui/RotatingLoader';

// Raised by PassLogic when none of the customer's passes can cover this session.
const NO_REDEEMABLE_PASS_DETAIL = 'No redeemable pass found';

type BookingBottomSheetProps = {
  scheduleSession: ScheduleSession | null;
  onClose: () => void;
};

export const BookingBottomSheet = ({ scheduleSession, onClose }: BookingBottomSheetProps) => {
  const { t, i18n } = useTranslation();
  const { account, email, passes, orders, addOrder, registerToWaitlist } = useManagePasses();
  const timeFormat = getTimeFormat(account);
  const isRtl = i18n.dir() === 'rtl';
  const dateFormat = isRtl ? 'dddd, D/M' : 'dddd, MM/DD';
  const language = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parallelActivities, setParallelActivities] = useState<ParallelActivityItem[]>([]);
  const [isParallelLoading, setIsParallelLoading] = useState(false);
  const [guestEnabled, setGuestEnabled] = useState(false);

  const waiverText =
    (account?.guest_pass_waiver_text as Record<string, string> | undefined)?.[language] ||
    account?.guest_pass_waiver_text?.en ||
    null;

  const guestForm = useFormik({
    initialValues: { firstname: '', lastname: '', email: '', phone: '', waiverAccepted: false },
    validationSchema: Yup.object({
      firstname: Yup.string().trim().required(t('manage_passes.schedule.guestFirstnameRequired')),
      lastname: Yup.string().trim().required(t('manage_passes.schedule.guestLastnameRequired')),
      email: Yup.string()
        .trim()
        .required(t('manage_passes.schedule.guestEmailRequired'))
        .email(t('manage_passes.schedule.guestEmailInvalid')),
      phone: Yup.string().trim().required(t('manage_passes.schedule.guestPhoneRequired')),
      waiverAccepted: waiverText
        ? Yup.boolean().oneOf([true], t('manage_passes.schedule.waiverRequired'))
        : Yup.boolean(),
    }),
    validateOnMount: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!scheduleSession) {
      setGuestEnabled(false);
      guestForm.resetForm();
    }
  }, [scheduleSession]);

  useEffect(() => {
    if (!scheduleSession?.eventId || !account || !email) {
      setParallelActivities([]);
      return;
    }
    setIsParallelLoading(true);
    PassService.customerParallelActivity(account.id, email, scheduleSession.eventId)
      .then(setParallelActivities)
      .catch(() => setParallelActivities([]))
      .finally(() => setIsParallelLoading(false));
  }, [scheduleSession?.eventId]);

  const isWaitlistMode = scheduleSession?.status === ScheduleSessionStatus.WAITLIST;
  const cancelHoursBefore = scheduleSession ? getPassCancelHoursBefore(account, scheduleSession.orderPageId) : 0;

  const { hasAvailablePunch, isPageLimitReached, pageLimit } = (() => {
    if (!scheduleSession) return { hasAvailablePunch: false, isPageLimitReached: false, pageLimit: null };

    let anyPassCoversPage = false;
    let anyPassHasPageLimit = false;

    for (const pass of passes) {
      const passOrderPage = getPassOrderPage(account, pass.order_page_id);
      if (passOrderPage && isPassActiveForDate(pass, passOrderPage, scheduleSession.date)) {
        const numPunches = getEffectiveNumPunches(pass, passOrderPage);
        const hasRemainingPunches = numPunches === null || getUsedPunches(pass) < numPunches;

        const redeemableItem = passOrderPage.redeemable_on?.find(
          (item) => item.order_page_id === scheduleSession.orderPageId,
        );
        const coversOrderPage =
          !passOrderPage.redeemable_on || passOrderPage.redeemable_on.length === 0 || redeemableItem !== undefined;

        if (coversOrderPage) {
          anyPassCoversPage = true;

          const usedOnPage =
            redeemableItem?.limit != null
              ? orders.filter(
                  (o) => pass.redeemed_order_ids?.includes(o.id) && o.order_page_id === scheduleSession.orderPageId,
                ).length
              : 0;
          const withinPageLimit = redeemableItem?.limit == null || usedOnPage < redeemableItem.limit;

          if (withinPageLimit) {
            if (hasRemainingPunches) return { hasAvailablePunch: true, isPageLimitReached: false, pageLimit: null };
          } else {
            anyPassHasPageLimit = true;
          }
        }
      }
    }

    const reachedLimit = anyPassCoversPage && anyPassHasPageLimit;
    const limitValue = reachedLimit
      ? passes
          .map(
            (p) =>
              getPassOrderPage(account, p.order_page_id)?.redeemable_on?.find(
                (item) => item.order_page_id === scheduleSession.orderPageId,
              )?.limit,
          )
          .find((l) => l != null) ?? null
      : null;
    return { hasAvailablePunch: false, isPageLimitReached: reachedLimit, pageLimit: limitValue };
  })();

  const guestSupportingPasses =
    scheduleSession && !isWaitlistMode
      ? passes.filter((pass) => {
          const passOrderPage = getPassOrderPage(account, pass.order_page_id);
          if (!passOrderPage || !isPassActiveForDate(pass, passOrderPage, scheduleSession.date)) return false;
          if (pass.guest_pass_remaining == null) return false;
          const numPunches = getEffectiveNumPunches(pass, passOrderPage);
          const hasRemainingPunches = numPunches === null || getUsedPunches(pass) < numPunches;
          if (!hasRemainingPunches) return false;
          const redeemableItem = passOrderPage.redeemable_on?.find(
            (item) => item.order_page_id === scheduleSession.orderPageId,
          );
          return redeemableItem?.allow_guest === true;
        })
      : [];

  const guestSupported = guestSupportingPasses.length > 0;
  const guestQuotaAvailable = guestSupportingPasses.some((p) => (p.guest_pass_remaining ?? 0) > 0);
  const emptyQuotaPassType = !guestQuotaAvailable
    ? getPassOrderPage(account, guestSupportingPasses[0]?.order_page_id)?.pass_type
    : undefined;

  const guestFormValid = !guestEnabled || guestForm.isValid;

  const handleConfirm = async () => {
    if (!scheduleSession || !account || !email) return;
    setIsLoading(true);
    setError(null);
    try {
      if (isWaitlistMode) {
        await registerToWaitlist({
          orderPageId: scheduleSession.orderPageId,
          experienceId: scheduleSession.experienceId,
          eventId: scheduleSession.eventId,
          date: scheduleSession.date,
          timeSeconds: scheduleSession.timeSeconds,
        });
      } else {
        const response = await PassService.customerPassCreateOrder({
          email,
          account_id: account.id,
          order_page_id: scheduleSession.orderPageId,
          ...(scheduleSession.eventId
            ? { event_id: scheduleSession.eventId }
            : { date: scheduleSession.date, time: scheduleSession.timeSeconds }),
          ...(guestEnabled
            ? {
                guest_details: {
                  firstname: guestForm.values.firstname.trim(),
                  lastname: guestForm.values.lastname.trim(),
                  email: guestForm.values.email.trim(),
                  phone: guestForm.values.phone.trim(),
                  waiver_accepted: waiverText ? guestForm.values.waiverAccepted : true,
                },
              }
            : {}),
        });
        await addOrder(response);
      }
      setIsLoading(false);
      onClose();
    } catch (e: any) {
      setIsLoading(false);
      if (isWaitlistMode) {
        setError(t('manage_passes.schedule.waitlistError'));
        return;
      }
      // The server's `detail` is untranslated English, so it cannot go on screen as-is. Map the one
      // reason a customer can act on and fall back to the generic booking error for the rest.
      const detail = e?.status === 400 ? e?.body?.detail : null;
      setError(
        detail === NO_REDEEMABLE_PASS_DETAIL
          ? t('manage_passes.schedule.noRedeemablePass')
          : t('manage_passes.schedule.bookingError'),
      );
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setError(null);
    onClose();
  };

  const ctaColor = isWaitlistMode ? 'warning' : 'primary';
  const ctaText = isWaitlistMode
    ? t('manage_passes.schedule.joinWaitlist')
    : t('manage_passes.schedule.confirmBooking');
  const ctaDisabled = !isWaitlistMode && (!hasAvailablePunch || isPageLimitReached || !guestFormValid);

  return (
    <BottomSheet
      open={!!scheduleSession}
      onClose={handleClose}
      ctaText={ctaText}
      ctaColor={ctaColor}
      ctaDisabled={ctaDisabled}
      isLoading={isLoading}
      onConfirm={handleConfirm}>
      {scheduleSession && (
        <>
          <Grid container flexDirection="column" gap={0.5}>
            <Typography variant="h6" fontWeight={700}>
              {scheduleSession.workshopName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {moment(scheduleSession.date).format(dateFormat)} ·{' '}
              {moment.utc(scheduleSession.timeSeconds * 1000).format(timeFormat)} -{' '}
              {moment.utc((scheduleSession.timeSeconds + scheduleSession.duration) * 1000).format(timeFormat)}
            </Typography>
            {scheduleSession.employeeName && (
              <Grid container alignItems="center" gap={0.5}>
                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {scheduleSession.employeeName}
                </Typography>
              </Grid>
            )}
          </Grid>

          {isParallelLoading ? (
            <RotatingLoader width="30px" />
          ) : (
            parallelActivities.length > 0 && (
              <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" fontWeight={700} mb={0.5}>
                  {t('manage_passes.schedule.alsoHappening')}
                </Typography>
                <Grid container flexDirection="column" gap={0.5}>
                  {parallelActivities.map((item, i) => (
                    <Grid key={i} container justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={700}>
                        {item.label?.[language]}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {moment.utc(item.time * 1000).format(timeFormat)} -{' '}
                        {moment.utc((item.time + item.duration) * 1000).format(timeFormat)}
                        {item.persons > 0 && ` · 👥 ${item.persons}`}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Alert>
            )
          )}

          {guestSupported && (
            <Grid container flexDirection="column" gap={1.5}>
              <Grid container flexDirection="column" gap={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={guestEnabled}
                      disabled={!guestQuotaAvailable}
                      onChange={(e) => setGuestEnabled(e.target.checked)}
                    />
                  }
                  label={t('manage_passes.schedule.bringGuest')}
                  sx={{ m: 0 }}
                />
                {!guestQuotaAvailable && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                    {isRecurringMonthly(emptyQuotaPassType)
                      ? t('manage_passes.schedule.guestQuotaEmptyMembership')
                      : t('manage_passes.schedule.guestQuotaEmptyPunch')}
                  </Typography>
                )}
              </Grid>
              <Collapse in={guestEnabled} unmountOnExit>
                <Grid container flexDirection="column" gap={1.5}>
                  <TextField
                    required
                    size="small"
                    name="firstname"
                    label={t('manage_passes.schedule.guestFirstname')}
                    value={guestForm.values.firstname}
                    onChange={guestForm.handleChange}
                    onBlur={guestForm.handleBlur}
                    error={guestForm.touched.firstname && !!guestForm.errors.firstname}
                    helperText={guestForm.touched.firstname && guestForm.errors.firstname}
                    fullWidth
                  />
                  <TextField
                    required
                    size="small"
                    name="lastname"
                    label={t('manage_passes.schedule.guestLastname')}
                    value={guestForm.values.lastname}
                    onChange={guestForm.handleChange}
                    onBlur={guestForm.handleBlur}
                    error={guestForm.touched.lastname && !!guestForm.errors.lastname}
                    helperText={guestForm.touched.lastname && guestForm.errors.lastname}
                    fullWidth
                  />
                  <TextField
                    required
                    size="small"
                    type="email"
                    name="email"
                    label={t('manage_passes.schedule.guestEmail')}
                    value={guestForm.values.email}
                    onChange={guestForm.handleChange}
                    onBlur={guestForm.handleBlur}
                    error={guestForm.touched.email && !!guestForm.errors.email}
                    helperText={guestForm.touched.email && guestForm.errors.email}
                    fullWidth
                  />
                  <TextField
                    required
                    size="small"
                    type="tel"
                    name="phone"
                    label={t('manage_passes.schedule.guestPhone')}
                    value={guestForm.values.phone}
                    onChange={guestForm.handleChange}
                    onBlur={guestForm.handleBlur}
                    error={guestForm.touched.phone && !!guestForm.errors.phone}
                    helperText={guestForm.touched.phone && guestForm.errors.phone}
                    fullWidth
                  />
                  {waiverText && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Grid
                        item
                        sx={{
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          '& p': { margin: 0, minHeight: '1em' },
                        }}
                        dangerouslySetInnerHTML={{ __html: waiverText }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="waiverAccepted"
                            checked={guestForm.values.waiverAccepted}
                            onChange={guestForm.handleChange}
                            onBlur={guestForm.handleBlur}
                          />
                        }
                        label={t('manage_passes.schedule.waiverAgree')}
                        sx={{ mt: 0.5 }}
                      />
                      {guestForm.touched.waiverAccepted && guestForm.errors.waiverAccepted && (
                        <Typography variant="caption" color="error" display="block">
                          {guestForm.errors.waiverAccepted}
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Grid>
              </Collapse>
            </Grid>
          )}

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : isWaitlistMode ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('manage_passes.schedule.waitlistInfo')}
            </Alert>
          ) : isPageLimitReached ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t('manage_passes.schedule.pageLimitReached', { count: pageLimit ?? undefined })}
            </Alert>
          ) : !hasAvailablePunch ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('manage_passes.schedule.noPunchesLeft')}
            </Alert>
          ) : cancelHoursBefore > 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t('manage_passes.schedule.cancellationWarning', { hours: cancelHoursBefore })}
            </Alert>
          ) : null}
        </>
      )}
    </BottomSheet>
  );
};
